/**
 * The enabled custom Person fields a superadmin has defined for this node.
 *
 * Public, rarely-changing data (mirrors use_node_info.js's pattern): the
 * first consumer fetches, everyone else reads the shared module-level value.
 * Used by CreateEntityForm/CreateEntityModal to render the
 * fields, by Entity.jsx to show and inline-edit them, and by the onboarding
 * wizard, which turns each one into its own page.
 *
 *   const personFields = usePersonFields();
 *   const { fields, loaded } = usePersonFieldsState(); // when the wait matters
 */

import { useState, useEffect } from 'react';
import { useApi } from './use_api';

let cachedFields = null;

// Called by use_person_field_admin.js after a superadmin mutation so the
// create/edit form and detail page pick up the change without a reload.
export function invalidatePersonFieldsCache() {
    cachedFields = null;
}

/**
 * The fields plus whether the first fetch has settled. `loaded` is tracked
 * separately from the list because a failed fetch leaves it `[]` forever, so a
 * consumer waiting on emptiness would hang rather than fall back (same shape
 * as use_node_info.js's `ready`). Only worth reaching for when the UI is
 * *driven by* the list rather than appended to — the onboarding wizard's page
 * count is the field count, so rendering before this is true would hand the
 * user a form missing the fields this node enabled.
 */
export function usePersonFieldsState() {
    const api = useApi();
    const [fields, setFields] = useState(cachedFields);
    const [loaded, setLoaded] = useState(cachedFields !== null);

    useEffect(() => {
        if (cachedFields) return;
        let alive = true;
        api.get('/api/person-fields/enabled', { cache: true })
            .then(data => {
                cachedFields = Array.isArray(data) ? data : [];
                if (alive) { setFields(cachedFields); setLoaded(true); }
            })
            .catch(() => { if (alive) { setFields([]); setLoaded(true); } });
        return () => { alive = false; };
    }, [api]);

    return { fields: fields || [], loaded };
}

export function usePersonFields() {
    return usePersonFieldsState().fields;
}

export default usePersonFields;
